/**
 * CS-3a/CS-3b — the Schedule builder: the Spec Book.
 *
 * The left column builds, the right column is the Spec Book, redrawn on every
 * change by the same renderer the shared page uses. A schedule can be saved by
 * name on the project and shared as a no-login link that always shows today's
 * list. Client answers, email, PDF generation and the other five presets are
 * later passes and appear here as disabled choices so the shape is honest.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { clipDimensions } from '@/pages/ClipDetail';
import ScheduleBook, { rowFromClip } from '@/components/ScheduleBook';
import { loadTenantBrand } from '@/components/tools/shared/tenantBrand';
import '@/styles/schedules.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { id: 'spec_book', name: 'Spec Book', on: true,
    note: 'images, room by room, approval state — no vendor, no SpecNum' },
  { id: 'trade', name: 'Trade schedule', note: 'next pass' },
  { id: 'purchasing', name: 'Purchasing schedule', note: 'next pass' },
  { id: 'finish', name: 'Finish schedule', note: 'next pass' },
  { id: 'plan', name: 'Plan-keyed schedule', note: 'next pass' },
  { id: 'master', name: 'Master (internal)', note: 'next pass' },
];

const COLUMNS = [
  { id: 'image', name: 'Image' },
  { id: 'where', name: 'Room · location' },
  { id: 'dims', name: 'Dimensions' },
  { id: 'price', name: 'Price' },
  { id: 'lead', name: 'Lead time' },
  { id: 'note', name: 'Trade note' },
  { id: 'approval', name: 'Approval state' },
];

const DEFAULT_OPTIONS = {
  organise: 'room',
  rooms: [],
  statuses: ['approved', 'pending', 'declined'],
  columns: { image: true, where: true, dims: true, price: false, lead: true, note: false, approval: true },
  cover: true,
};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function ScheduleBuilderPage() {
  const { projectId, scheduleId: idInPath } = useParams();
  // "new" is the unsaved Spec Book; any other id is one already saved.
  const scheduleId = idInPath === 'new' ? null : idInPath;
  const navigate = useNavigate();

  const [clips, setClips] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [project, setProject] = useState(null);
  const [brand, setBrand] = useState({ companyName: '', logoUrl: '' });
  const [loading, setLoading] = useState(true);

  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [saved, setSaved] = useState(null);          // the saved schedule, once there is one
  const [name, setName] = useState('Spec Book');
  const [naming, setNaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sharePanel, setSharePanel] = useState(false);
  const [copied, setCopied] = useState(false);

  const setColumn = (id) => setOptions((o) => ({ ...o, columns: { ...o.columns, [id]: !o.columns[id] } }));
  const toggle = (key, value) => setOptions((o) => ({
    ...o,
    [key]: (o[key] || []).includes(value)
      ? (o[key] || []).filter((v) => v !== value) : [...(o[key] || []), value],
  }));

  useEffect(() => {
    let live = true;
    Promise.all([
      axios.get(`${API}/clips`, { params: { project_id: projectId } }).then(({ data }) => data).catch(() => []),
      axios.get(`${API}/projects/${projectId}/rooms`).then(({ data }) => data.rooms || []).catch(() => []),
      axios.get(`${API}/projects/${projectId}`).then(({ data }) => data).catch(() => null),
      loadTenantBrand(),
    ]).then(([clipList, roomList, projectData, tenant]) => {
      if (!live) return;
      setClips(Array.isArray(clipList) ? clipList : (clipList.clips || []));
      setRooms(roomList.filter((r) => r.id));
      setProject(projectData);
      setBrand(tenant);
      setLoading(false);
    });
    return () => { live = false; };
  }, [projectId]);

  // Opening a saved schedule loads its choices back into the builder.
  useEffect(() => {
    if (!scheduleId) { setSaved(null); return; }
    axios.get(`${API}/projects/${projectId}/schedules`)
      .then(({ data }) => {
        const found = (data.schedules || []).find((s) => s.id === scheduleId);
        if (!found) { toast.error('That schedule is not on this project'); return; }
        setSaved(found);
        setOptions(found.options);
        setName(found.name);
      })
      .catch(() => toast.error('Could not open that schedule'));
  }, [projectId, scheduleId]);

  // CS-2 — the book prints on paper colours whichever skin is on screen.
  useEffect(() => {
    const before = () => {
      document.body.dataset.kmSkinBeforePrint = document.body.dataset.kmSkin || '';
      document.body.dataset.kmSkin = 'studio';
    };
    const after = () => {
      const previous = document.body.dataset.kmSkinBeforePrint;
      if (previous) document.body.dataset.kmSkin = previous; else delete document.body.dataset.kmSkin;
      delete document.body.dataset.kmSkinBeforePrint;
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);

  // PR-1 — the Spec Book is the document while this page is up.
  useEffect(() => {
    document.body.dataset.kmPrint = 'spec-book';
    return () => { delete document.body.dataset.kmPrint; };
  }, []);

  const rows = useMemo(() => clips.map((clip) =>
    rowFromClip(clip, options.columns, clipDimensions)), [clips, options.columns]);

  // The room chips offer the groups this project actually has, whatever the
  // status filter is doing.
  const groupChips = useMemo(() => {
    const seen = new Map();
    clips.forEach((clip) => {
      if (options.organise === 'name') { seen.set('all', 'All selections'); return; }
      if (options.organise === 'category') {
        const category = String(clip.spec_category || '').trim() || 'Uncategorized';
        seen.set(category, category);
        return;
      }
      const room = clip.room_id ? rooms.find((r) => r.id === clip.room_id) : null;
      if (room) { seen.set(room.id, room.name); return; }
      const text = String(clip.room || '').trim() || 'Unassigned';
      seen.set(text, text);
    });
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [clips, rooms, options.organise]);

  const dirty = saved ? !same(saved.options, options) : false;

  const save = useCallback(async () => {
    setBusy(true);
    try {
      if (saved) {
        const { data } = await axios.patch(`${API}/schedules/${saved.id}`, { options });
        setSaved(data);
        toast.success('Saved');
      } else {
        const { data } = await axios.post(`${API}/projects/${projectId}/schedules`, {
          name: name.trim() || 'Spec Book', preset: 'spec_book', options,
        });
        setSaved(data);
        setNaming(false);
        toast.success('Saved');
        navigate(`/projects/${projectId}/schedules/${data.id}`, { replace: true });
      }
    } catch {
      toast.error('Could not save that');
    }
    setBusy(false);
  }, [saved, options, name, projectId, navigate]);

  const rename = async (value) => {
    const next = value.trim() || 'Spec Book';
    setName(next);
    setNaming(false);
    if (!saved || next === saved.name) return;
    try {
      const { data } = await axios.patch(`${API}/schedules/${saved.id}`, { name: next });
      setSaved(data);
      toast.success('Renamed');
    } catch {
      toast.error('Could not rename that');
    }
  };

  const startShare = async () => {
    setSharePanel(true);
    if (!saved || saved.share?.active) return;
    try {
      const { data } = await axios.post(`${API}/schedules/${saved.id}/share`);
      setSaved((s) => ({ ...s, share: { ...(s.share || {}), token: data.token, url: data.url, active: true } }));
    } catch {
      toast.error('Could not make a link');
    }
  };

  // CS-3c — the firm decides whether the link takes answers.
  const setAllowAnswers = async (allow) => {
    try {
      const { data } = await axios.patch(`${API}/schedules/${saved.id}/share`, { allow_answers: allow });
      setSaved(data);
    } catch {
      toast.error('Could not change that');
    }
  };

  const stopShare = async () => {
    try {
      await axios.delete(`${API}/schedules/${saved.id}/share`);
      setSaved((s) => ({ ...s, share: { ...s.share, active: false } }));
      toast.success('Stopped sharing');
    } catch {
      toast.error('Could not stop that');
    }
  };

  const shareUrl = saved?.share?.active ? saved.share.url : '';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center gap-2 flex-wrap mb-4 print:hidden" data-testid="schedule-bar">
          <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}/desk?tab=clips`)}
            data-testid="schedule-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Project Specifications
          </Button>
          {saved && naming ? (
            <Input className="w-64" defaultValue={name} autoFocus
              onBlur={(e) => rename(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') rename(e.target.value); }}
              data-testid="schedule-name-field" />
          ) : (
            <h1 className="font-heading text-xl cursor-pointer" onClick={() => saved && setNaming(true)}
              title={saved ? 'Click to rename' : undefined} data-testid="schedule-title">
              {saved ? name : 'Spec Book'}
            </h1>
          )}
          <span className="flex-1" />
          {saved && dirty ? (
            <Button variant="outline" onClick={save} disabled={busy} data-testid="schedule-save-changes-btn">
              Save changes
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => window.print()} data-testid="schedule-print-btn">
            <Printer className="w-4 h-4 mr-2" />Print / Save PDF
          </Button>
          {saved ? (
            <Button variant="outline" onClick={startShare} data-testid="schedule-share-btn">Share…</Button>
          ) : (
            <Button variant="outline" disabled title="Save this Spec Book first"
              data-testid="schedule-share-btn">Save first</Button>
          )}
        </div>

        {sharePanel && saved ? (
          <div className="rounded-lg border bg-muted/30 p-3 mb-4 print:hidden" data-testid="schedule-share-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <Input className="flex-1" readOnly value={shareUrl}
                data-testid="schedule-share-url" />
              <Button size="sm" variant="outline" disabled={!shareUrl}
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                data-testid="schedule-share-copy">
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              {saved.share?.active ? (
                <Button size="sm" variant="outline" onClick={stopShare}
                  data-testid="schedule-share-stop">Stop sharing</Button>
              ) : null}
            </div>
            <div className="ksh-chk mt-2" style={{ gridTemplateColumns: '1fr' }}>
              <label className={saved.share?.allow_answers === false ? 'off' : ''}
                onClick={() => setAllowAnswers(saved.share?.allow_answers === false)}
                data-testid="schedule-allow-answers">
                <span className={`box ${saved.share?.allow_answers === false ? '' : 'on'}`} />
                Client can approve or decline on this link
              </label>
            </div>
            <div className="text-sm text-muted-foreground mt-2" data-testid="schedule-share-note">
              No login · always shows today&apos;s list · Prices are{' '}
              {options.columns.price ? 'on' : 'off'} in this Spec Book
              {saved.share?.client_label
                ? ` · Answers are recorded as ${saved.share.client_label}` : ''}
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <div className="ksh km-doc km-doc--landscape" data-testid="schedule-builder">
            <div className="ksh__opts" data-km-noprint>
              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">For whom</div>
                <div className="ksh-preset" data-testid="schedule-presets">
                  {PRESETS.map((preset) => (
                    <div key={preset.id} className={preset.on ? 'on' : 'off'}
                      data-testid={`schedule-preset-${preset.id}`}>
                      <span className="r" />
                      <span>
                        {preset.name}
                        <small>{preset.note}</small>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">Organise by</div>
                <div className="ksh-seg" data-testid="schedule-groupby">
                  {[['room', 'Room'], ['category', 'Spec Category'], ['name', 'Name']].map(([id, label]) => (
                    <span key={id} className={options.organise === id ? 'on' : ''}
                      onClick={() => setOptions((o) => ({ ...o, organise: id, rooms: [] }))}
                      data-testid={`schedule-groupby-${id}`}>{label}</span>
                  ))}
                </div>
              </div>

              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">Include</div>
                <div className="ksh-seg" data-testid="schedule-rooms">
                  <span className={options.rooms.length ? '' : 'on'}
                    onClick={() => setOptions((o) => ({ ...o, rooms: [] }))}
                    data-testid="schedule-rooms-all">All rooms</span>
                  {groupChips.map((chip) => (
                    <span key={chip.key} className={options.rooms.includes(chip.key) ? 'on' : ''}
                      onClick={() => toggle('rooms', chip.key)}
                      data-testid={`schedule-room-${chip.key}`}>{chip.label}</span>
                  ))}
                </div>
                <div className="ksh-seg" data-testid="schedule-statuses">
                  {[['approved', 'Approved'], ['pending', 'Pending'], ['declined', 'Declined']].map(([id, label]) => (
                    <span key={id} className={options.statuses.includes(id) ? 'on' : ''}
                      onClick={() => toggle('statuses', id)}
                      data-testid={`schedule-status-${id}`}>{label}</span>
                  ))}
                </div>
              </div>

              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">Columns</div>
                <div className="ksh-chk" data-testid="schedule-columns">
                  {COLUMNS.map((column) => (
                    <label key={column.id} className={options.columns[column.id] ? '' : 'off'}
                      onClick={() => setColumn(column.id)}
                      data-testid={`schedule-column-${column.id}`}>
                      <span className={`box ${options.columns[column.id] ? 'on' : ''}`} />{column.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">Front matter</div>
                <div className="ksh-chk">
                  <label className={options.cover ? '' : 'off'}
                    onClick={() => setOptions((o) => ({ ...o, cover: !o.cover }))}
                    data-testid="schedule-cover">
                    <span className={`box ${options.cover ? 'on' : ''}`} />Cover page
                  </label>
                </div>
              </div>

              <div className="sec">
                <div className="lab text-xs uppercase tracking-wide text-muted-foreground">Output</div>
                <div className="ksh__out text-sm">
                  <span>Print / Save PDF · letter landscape</span>
                </div>
                {saved ? (
                  <div className="ksh__out text-sm" data-testid="schedule-saved-note">
                    <span>Saved as “{name}”{saved.share?.active ? ' · shared' : ''}</span>
                  </div>
                ) : naming ? (
                  <div className="ksh__out">
                    <Input className="h-8" value={name} autoFocus
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                      data-testid="schedule-save-name" />
                    <Button size="sm" variant="outline" onClick={save} disabled={busy}
                      data-testid="schedule-save-confirm">Save</Button>
                  </div>
                ) : (
                  <div className="ksh__out">
                    <Button size="sm" variant="outline" onClick={() => setNaming(true)}
                      data-testid="schedule-save-btn">Save as…</Button>
                  </div>
                )}
              </div>
            </div>

            <div className="ksh__prev" data-testid="schedule-preview">
              <div className="ksh__prevhd" data-km-noprint>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Preview</span>
                <span className="sp" />
                {dirty ? <span className="text-xs text-muted-foreground">unsaved changes</span> : null}
              </div>

              {loading ? <div className="ksh-pg">Reading this project…</div> : (
                <ScheduleBook
                  firm={{ name: brand.companyName, mark: brand.logoUrl }}
                  project={{ name: project?.name, client_name: project?.client_name }}
                  schedule={{ name, options }}
                  rooms={rooms}
                  rows={rows} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
